import { Box } from '@mui/material';
import { AdvancedMarker, Map } from '@vis.gl/react-google-maps';
import { MapPin } from 'lucide-react';

import { GOOGLE_MAPS_MAP_ID } from '@/types/maps';

const PROPERTY_MAP_ID = 'onboarding-property-map';

export interface PropertyLocationMapProps {
  latitude?: number;
  longitude?: number;
  /** Fixed height for a stacked (mobile) layout — omit to flex-fill a row. */
  height?: number;
}

// The same black-and-white styled map used elsewhere in the app
// (NeighborhoodMapCard, PropertyMap), not a generic placeholder graphic.
export const PropertyLocationMap = ({ latitude, longitude, height }: PropertyLocationMapProps) => {
  const hasLocation = latitude != null && longitude != null;

  return (
    <Box
      sx={{
        flex: height ? 'none' : 1,
        height,
        borderRadius: '24px',
        overflow: 'hidden',
        position: 'relative',
        bgcolor: 'var(--tx-glass-panel)',
        backgroundImage: 'var(--tx-surface-light)',
        boxShadow: 'var(--tx-shadow)',
      }}>
      {hasLocation ? (
        <Box
          key={`${latitude},${longitude}`}
          sx={{
            width: '100%',
            height: '100%',
            animation: 'onboarding-map-reveal 500ms ease',
            '@keyframes onboarding-map-reveal': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}>
          <Map
            id={PROPERTY_MAP_ID}
            mapId={GOOGLE_MAPS_MAP_ID}
            defaultCenter={{ lat: latitude, lng: longitude }}
            defaultZoom={18}
            style={{ width: '100%', height: '100%' }}
            gestureHandling="greedy"
            disableDefaultUI>
            <AdvancedMarker position={{ lat: latitude, lng: longitude }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: '50% 50% 50% 0',
                  rotate: '-45deg',
                  bgcolor: 'var(--tx-lime)',
                  color: 'var(--tx-on-lime)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                }}>
                <MapPin size={20} style={{ rotate: '45deg', transform: 'translateY(2px)' }} />
              </Box>
            </AdvancedMarker>
          </Map>
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundImage: [
              'linear-gradient(28deg, transparent 47%, rgba(187,183,175,0.13) 48%, rgba(187,183,175,0.13) 51%, transparent 52%)',
              'linear-gradient(150deg, transparent 42%, rgba(187,183,175,0.09) 43%, rgba(187,183,175,0.09) 46%, transparent 47%)',
            ].join(', '),
          }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              display: 'grid',
              placeItems: 'center',
              borderRadius: '50% 50% 50% 0',
              rotate: '-45deg',
              bgcolor: 'var(--tx-fill-2)',
              color: 'var(--tx-text)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            }}>
            <MapPin size={18} style={{ rotate: '45deg' }} />
          </Box>
        </Box>
      )}
    </Box>
  );
};
